/**
 * REST + WS helpers for the Recordings feature.
 *
 * REST: upload, from-url, audio bytes, transcript fetch — anything that
 * benefits from streaming or multipart goes over HTTP. WS: list / get /
 * update / delete / retry / promote + server-pushed RecordingStatusEvent.
 *
 * Mirrors the documents feature's split (FormData over HTTP, typed envelopes
 * over WS); see `web/src/features/documents/documentsStore.ts`.
 */

import { create as createProto } from '@bufbuild/protobuf';
import { getConfig } from '@/lib/config';
import { getAuthHeaders } from '@/lib/http';
import { HttpError, httpWireError } from '@/lib/errors/http';
import type { WireError } from '@/lib/errors/catalog';
import { createEnvelope } from '@/lib/protobuf/codec';
import { ErrorCode } from '@/lib/protobuf/gen/errors/errors_pb';
import {
  CancelTranscriptionRequestSchema,
  DeleteRecordingRequestSchema,
  GetRecordingRequestSchema,
  ListRecordingsRequestSchema,
  PromoteRecordingRequestSchema,
  type Recording,
  RetryTranscriptionRequestSchema,
  type Transcript,
  UpdateRecordingRequestSchema,
} from '@/lib/protobuf/gen/recordings/recordings_pb';
import { wsManager } from '@/lib/websocket';
import { normalizeRecordingBlob } from './webmTimestamps';

// ---------- REST ----------

/**
 * The transcript route always returns a status (`pending` / `transcribing` /
 * `ready` / `failed`) and *optionally* a transcript when status === 'ready'.
 */
export interface TranscriptResponse {
  recordingId: string;
  status: string;
  failureReason?: string;
  transcript: Transcript | null;
}

export interface UploadOpts {
  title?: string;
  signal?: AbortSignal;
  /** E1 Phase 8: tag this recording's ASR spend to a workspace (grant-validated server-side). */
  workspaceId?: string;
  /**
   * BILL-05: the client's best knowledge of the audio length (mic timer or file
   * metadata). The server sizes the credit hold from it; sent as `duration_secs`
   * (whole seconds, rounded up) only when finite and positive.
   */
  durationSecs?: number;
}

async function readErrorBody(res: Response): Promise<string> {
  try {
    return (await res.text()) || res.statusText || `HTTP ${res.status}`;
  } catch {
    return res.statusText || `HTTP ${res.status}`;
  }
}

/**
 * Thrown when an upload is rejected with HTTP 423 Locked — the per-user
 * session DEK is unavailable server-side, so the bytes can't be encrypted at
 * rest yet. This is recoverable (unlike a 4xx on the bytes themselves): once
 * the WS reconnects and the crypto handshake re-runs, the same bytes upload
 * fine. Callers catch this to HOLD the recording for retry rather than
 * discarding it. The common trigger is a mobile screen-lock suspending the
 * WS mid-recording (Android Firefox).
 */
export class SessionLockedError extends Error {
  constructor(message = 'unlock your session') {
    super(message);
    this.name = 'SessionLockedError';
  }
}

/**
 * Thrown for HTTP 429 on the upload / from-url routes (BILL-05 per-user
 * limits). An `HttpError` (status 429) that also carries the server's
 * `Retry-After`, so the store can keep the capture and retry once it elapses
 * instead of dropping it.
 */
export class RateLimitedError extends HttpError {
  readonly retryAfterSecs: number;
  constructor(wire: WireError, retryAfterSecs: number) {
    super(wire, 429);
    this.name = 'RateLimitedError';
    this.retryAfterSecs = retryAfterSecs;
  }
}

/**
 * Thrown for HTTP 503 ERROR_CODE_UNAVAILABLE on the upload / from-url routes
 * (BILL-20f plan decision P1: the tier could not be read, so the server
 * refuses before ingest and asks for a retry). An `HttpError` (status 503)
 * that carries `Retry-After` when the server sends one, else the 60 s
 * default, so the store can keep a capture and retry it instead of dropping it.
 */
export class UnavailableError extends HttpError {
  readonly retryAfterSecs: number;
  constructor(wire: WireError, retryAfterSecs: number) {
    super(wire, 503);
    this.name = 'UnavailableError';
    this.retryAfterSecs = retryAfterSecs;
  }
}

/** The wait assumed when `Retry-After` is absent or not a delay-seconds value. */
const DEFAULT_RETRY_AFTER_SECS = 60;

/**
 * Parses `Retry-After` as a positive integer of seconds. The HTTP-date form is
 * not emitted by our server, so anything else falls back to 60 s.
 */
function parseRetryAfter(header: string | null): number {
  const trimmed = header?.trim() ?? '';
  if (!/^\d+$/.test(trimmed)) return DEFAULT_RETRY_AFTER_SECS;
  const secs = Number(trimmed);
  return secs > 0 ? secs : DEFAULT_RETRY_AFTER_SECS;
}

/** Throws the typed error for a non-OK upload / from-url response. */
async function throwUploadError(res: Response): Promise<never> {
  if (res.status === 423) throw new SessionLockedError(await readErrorBody(res));
  if (res.status === 429) {
    const e = await httpWireError(res);
    throw new RateLimitedError(e.wire, parseRetryAfter(res.headers.get('Retry-After')));
  }
  const e = await httpWireError(res);
  // Narrow: only a 503 whose code is UNAVAILABLE (sent by httperr, or derived
  // from the bare status) — never another 5xx.
  if (res.status === 503 && e.wire.code === ErrorCode.UNAVAILABLE) {
    throw new UnavailableError(e.wire, parseRetryAfter(res.headers.get('Retry-After')));
  }
  throw e;
}

/** Multipart upload of an audio file. */
export async function uploadRecording(file: File, opts: UploadOpts = {}): Promise<Recording> {
  const formData = new FormData();
  formData.append('file', file);
  if (opts.title) formData.append('title', opts.title);
  if (opts.workspaceId) formData.append('workspace_id', opts.workspaceId);
  const d = opts.durationSecs;
  if (d !== undefined && Number.isFinite(d) && d > 0) {
    formData.append('duration_secs', String(Math.ceil(d)));
  }

  const init: RequestInit = {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  };
  if (opts.signal) init.signal = opts.signal;
  const res = await fetch(`${getConfig().apiBase}/api/v1/recordings`, init);
  if (!res.ok) await throwUploadError(res);
  return (await res.json()) as Recording;
}

/** Server-side fetch from a URL (e.g. a YouTube auto-captioned video). */
export async function importFromURL(
  url: string,
  title?: string,
  signal?: AbortSignal,
  workspaceId?: string,
): Promise<Recording> {
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    // workspaceId (lowerCamelCase) matches the Go BodyParser field on /from-url.
    body: JSON.stringify({ url, ...(title ? { title } : {}), ...(workspaceId ? { workspaceId } : {}) }),
  };
  if (signal) init.signal = signal;
  const res = await fetch(`${getConfig().apiBase}/api/v1/recordings/from-url`, init);
  if (!res.ok) await throwUploadError(res);
  return (await res.json()) as Recording;
}

/**
 * Returns the bare URL of the audio endpoint. Not safe to pass directly to
 * `<audio src>` — the browser fetches src URLs without our Bearer token, so
 * the backend's auth.RequireAuth() middleware would return 401. Use
 * fetchAudioBlobURL below for the player.
 */
export function audioURL(recordingId: string): string {
  return `${getConfig().apiBase}/api/v1/recordings/${encodeURIComponent(recordingId)}/audio`;
}

/**
 * Fetches the audio bytes with the Authorization header injected, then wraps
 * them in a blob URL the `<audio>` element can play. The caller MUST revoke
 * the URL via URL.revokeObjectURL when the player unmounts or the recording
 * changes — otherwise the blob lives in memory for the page lifetime.
 *
 * This is a memory cost (the full clip is buffered before the first byte of
 * playback) but our recordings are short (minutes, not hours) and the
 * alternative — signed query-parameter URLs — needs server-side wiring.
 */
export async function fetchAudioBlobURL(recordingId: string, signal?: AbortSignal): Promise<string> {
  const init: RequestInit = { headers: getAuthHeaders() };
  if (signal) init.signal = signal;
  const res = await fetch(audioURL(recordingId), init);
  if (!res.ok) throw await httpWireError(res);
  const blob = await res.blob();
  // Chrome-on-Android MediaRecorder writes the capture clock into cluster
  // timestamps, so `<audio>` reports a ~100-minute timeline for a 3-minute clip
  // and parks the audio at the end (prod 2026-08-05). Rebase to zero on read —
  // a no-op for every other file, and the only fix that also repairs
  // recordings captured before this shipped. Falls back to the original bytes
  // on any parse failure. See webmTimestamps.ts.
  const normalized = await normalizeRecordingBlob(blob);
  return URL.createObjectURL(normalized);
}

/**
 * Speaker-recognition active learning. Marks (or clears) a diarized cluster as
 * the account holder. `is_self=true` enrolls the cluster's embedding into the
 * user's voiceprint and sets `self_speaker_label`; `is_self=false` clears it and
 * down-weights the sample. Returns the updated recording (proto-JSON shape, so
 * `selfSpeakerLabel` etc. cast cleanly onto the proto-gen `Recording`).
 *
 * Snake_case body (`label`, `is_self`) per the Go handler's hand-rolled request
 * struct; the *response* is the canonical proto-JSON `Recording` (lowerCamelCase).
 */
export async function setSelfSpeaker(
  recordingId: string,
  label: string,
  isSelf: boolean,
  signal?: AbortSignal,
): Promise<Recording> {
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ label, is_self: isSelf }),
  };
  if (signal) init.signal = signal;
  const res = await fetch(
    `${getConfig().apiBase}/api/v1/recordings/${encodeURIComponent(recordingId)}/self-speaker`,
    init,
  );
  if (res.status === 423) throw new SessionLockedError(await readErrorBody(res));
  if (!res.ok) throw await httpWireError(res);
  return (await res.json()) as Recording;
}

/** Fetch the JSON transcript document. */
export async function fetchTranscript(recordingId: string, signal?: AbortSignal): Promise<TranscriptResponse> {
  const init: RequestInit = { headers: getAuthHeaders() };
  if (signal) init.signal = signal;
  const res = await fetch(
    `${getConfig().apiBase}/api/v1/recordings/${encodeURIComponent(recordingId)}/transcript`,
    init,
  );
  if (!res.ok) throw await httpWireError(res);
  return (await res.json()) as TranscriptResponse;
}

// ---------- WS envelopes ----------

export function sendListRecordings(): void {
  const req = createProto(ListRecordingsRequestSchema, {});
  wsManager.sendEnvelope(createEnvelope({ case: 'listRecordingsRequest', value: req }));
}

export function sendGetRecording(recordingId: string): void {
  const req = createProto(GetRecordingRequestSchema, { recordingId });
  wsManager.sendEnvelope(createEnvelope({ case: 'getRecordingRequest', value: req }));
}

export function sendDeleteRecording(recordingId: string): void {
  const req = createProto(DeleteRecordingRequestSchema, { recordingId });
  wsManager.sendEnvelope(createEnvelope({ case: 'deleteRecordingRequest', value: req }));
}

export function sendRetryTranscription(recordingId: string): void {
  const req = createProto(RetryTranscriptionRequestSchema, { recordingId });
  wsManager.sendEnvelope(createEnvelope({ case: 'retryTranscriptionRequest', value: req }));
}

/**
 * Stop an in-flight transcription (REC-CANCEL-01). The server accepts this only
 * from `pending` / `extracting` / `transcribing` and answers with the updated
 * recording in `cancelTranscriptionResponse` — which `registerRecordingHandlers`
 * must route, or the response is dropped on the floor.
 */
export function sendCancelTranscription(recordingId: string): void {
  const req = createProto(CancelTranscriptionRequestSchema, { recordingId });
  wsManager.sendEnvelope(createEnvelope({ case: 'cancelTranscriptionRequest', value: req }));
}

export function sendPromoteRecording(recordingId: string): void {
  const req = createProto(PromoteRecordingRequestSchema, { recordingId });
  wsManager.sendEnvelope(createEnvelope({ case: 'promoteRecordingRequest', value: req }));
}

/**
 * Update fields. Strictly respects the protobuf-empty-string rule (CLAUDE.md):
 * only fields the caller passes are populated on the envelope. Title and
 * collectionId are `optional` on the proto, so an undefined means "don't
 * touch"; speakers/tags are repeated, so a nil/empty array means "don't touch."
 */
export interface UpdateRecordingFields {
  title?: string;
  tags?: string[];
  speakers?: string[];
  collectionId?: string;
  // R22 agentic ambient capture: per-recording opt-in for auto-acting on the
  // transcript (extract todos / run research via opted-in workflows).
  autoActOptIn?: boolean;
}

export function sendUpdateRecording(recordingId: string, fields: UpdateRecordingFields): void {
  const payload: {
    recordingId: string;
    title?: string;
    tags?: string[];
    speakers?: string[];
    collectionId?: string;
    autoActOptIn?: boolean;
  } = { recordingId };
  if (fields.title !== undefined) payload.title = fields.title;
  if (fields.tags !== undefined && fields.tags.length > 0) payload.tags = fields.tags;
  if (fields.speakers !== undefined && fields.speakers.length > 0) payload.speakers = fields.speakers;
  if (fields.collectionId !== undefined) payload.collectionId = fields.collectionId;
  if (fields.autoActOptIn !== undefined) payload.autoActOptIn = fields.autoActOptIn;

  const req = createProto(UpdateRecordingRequestSchema, payload);
  wsManager.sendEnvelope(createEnvelope({ case: 'updateRecordingRequest', value: req }));
}
